// 15.E ч.2 — оплата тарифа через СБП (Точка, B2C QR). Заказчик выбирает тариф →
// создаётся динамический QR → показываем картинку → поллим статус → при оплате
// тариф активируется автоматически (бэк: GET .../payment/{qrc_id}).
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/Modal';

interface PayResp {
  qrc_id: string; payload: string | null; image: string | null;
  image_media_type: string; amount_rub: number; tariff: string;
}
interface StatusResp { paid: boolean; status: string }

// Полный — первым и «рекомендуем» (якорим на «До готовности»: там полный цикл с
// проектировщиком; зазор всего +2000 ₽). Экспресс — приглушённый вариант.
const TARIFFS = [
  { key: 'full', name: 'Сопровождение до экспертизы', price: 12000, recommended: true,
    desc: 'Безлимит прогонов и редакций, сравнение версий ПД, довести проект до готовности.' },
  { key: 'express', name: 'Экспресс-проверка', price: 10000, recommended: false,
    desc: 'Разовый прогон каждой проверки. Без цикла редакций и сравнения версий.' },
];

export function PaymentModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [qr, setQr] = useState<PayResp | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);

  const start = async (tariff: string) => {
    setBusy(true); setErr(null);
    try {
      setQr(await api.post<PayResp>(`/projects/${projectId}/payment`, { tariff }));
    } catch {
      setErr('Не удалось создать платёж. Попробуйте позже или обратитесь в поддержку.');
    } finally {
      setBusy(false);
    }
  };

  // Поллинг статуса каждые 4 с; при оплате — активируем тариф (инвалидация проекта).
  useEffect(() => {
    if (!qr || paid) return;
    const id = window.setInterval(async () => {
      try {
        const s = await api.get<StatusResp>(`/projects/${projectId}/payment/${qr.qrc_id}`);
        if (s.paid) {
          setPaid(true);
          qc.invalidateQueries({ queryKey: ['project', projectId] });
        }
      } catch { /* сеть/бэк — просто ждём следующий тик */ }
    }, 4000);
    return () => window.clearInterval(id);
  }, [qr, paid, projectId, qc]);

  return (
    <Modal title="Оплата тарифа" onClose={onClose} maxWidth={460}>
      {paid ? (
        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
          <CheckCircle2 size={44} color="#10B981" />
          <h4 className="font-heading text-ink" style={{ fontSize: 17, fontWeight: 700, margin: '12px 0 4px' }}>
            Оплата прошла
          </h4>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 18 }}>
            Тариф активирован — можно запускать проверки.
          </p>
          <button type="button" onClick={onClose}
            style={{ padding: '9px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                     background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer' }}>
            Готово
          </button>
        </div>
      ) : !qr ? (
        <div>
          <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
            Выберите тариф. Оплата — по QR через СБП из любого банк-приложения; тариф
            активируется автоматически после оплаты.
          </p>
          {TARIFFS.map((t) => (
            <button key={t.key} type="button" onClick={() => start(t.key)} disabled={busy}
              style={{
                display: 'block', width: '100%', textAlign: 'left', marginBottom: 10,
                padding: '12px 14px', borderRadius: 12,
                border: t.recommended ? '2px solid #0A1628' : '1px solid #E5E7EB',
                background: t.recommended ? '#F8FAFC' : '#fff',
                cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="text-ink" style={{ fontSize: t.recommended ? 15 : 13.5, fontWeight: 700 }}>
                  {t.name} · {t.price.toLocaleString('ru-RU')} ₽
                </span>
                {t.recommended && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fff', background: '#0A1628',
                                 padding: '2px 8px', borderRadius: 20 }}>РЕКОМЕНДУЕМ</span>
                )}
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.45 }}>{t.desc}</div>
            </button>
          ))}
          {busy && <p className="text-muted" style={{ fontSize: 12 }}>Создаём платёж…</p>}
          {err && <p style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 6 }}>{err}</p>}
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <p className="text-ink" style={{ fontSize: 14, marginBottom: 12 }}>
            {TARIFFS.find((t) => t.key === qr.tariff)?.name} — <b>{qr.amount_rub.toLocaleString('ru-RU')} ₽</b>
          </p>
          {qr.image ? (
            <img src={`data:${qr.image_media_type};base64,${qr.image}`} alt="QR-код для оплаты по СБП"
                 style={{ width: 260, height: 260, margin: '0 auto', display: 'block' }} />
          ) : qr.payload ? (
            <a href={qr.payload} target="_blank" rel="noreferrer" style={{ color: '#2563EB', fontSize: 13 }}>
              Открыть ссылку для оплаты по СБП
            </a>
          ) : null}
          <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: '14px 0 6px' }}>
            Отсканируйте QR в банк-приложении и подтвердите оплату. Окно можно не закрывать —
            тариф активируется автоматически.
          </p>
          <p className="text-muted" style={{ fontSize: 12 }}>Ожидаем оплату…</p>
        </div>
      )}
    </Modal>
  );
}
